// ===== ĐƠN THU NỢ (type 11): CHI TIẾT THU TIỀN (6 Ô CỘT TRÁI) + PHÂN BỔ TIỀN DƯ =====
// Cột trái có 6 ô: Tiền SP, VAT, Chênh lệch, Gói cước, Phí, Công.
// 2 chế độ:
//   - Tự động: tick đơn theo Tiền thực nhận, 6 ô = tiền nợ các đơn đã tick (khoá, không sửa).
//   - Tùy chỉnh: kế toán nhập 6 ô -> tick đơn theo từng cột. Số trên ô = nợ các đơn đã tick + phần dư của cột
//     (biến "them" = dư từng cột, chưa vào được đơn nào).
// Tiền dư (cả 2 chế độ) mặc định phân bổ vào 1 đơn chưa tick, phần còn lại vào dư nợ.

// Bật bản mới của đơn thu nợ. Bật/tắt ở src/lib/featureFlags.ts.
// Đường dẫn tương đối (không dùng alias @lib) để file test biên dịch bằng tsc vẫn chạy.
// shortcut: hằng số cố định theo bản build; cần bật/tắt theo môi trường thì đổi sang cờ OMConfig.
export { THU_NO_NHAP_THEM } from '../../../../../../lib/featureFlags';
import { getPaymentStatus } from '../../../layoutContentUWR/hook/debtOrderFilter';

// 'auto' = Tự động, 'custom' = Tùy chỉnh
export type TCheDoThuNo = 'auto' | 'custom';

// Chế độ gửi / nhận với server qua field debtAllocationMode: 1 = Tự động (Auto), 2 = Tùy chỉnh (Manual).
// Tùy chỉnh: server lấy 6 ô làm tổng đã thu, phần còn lại sau đơn tick + trả một phần vào dư nợ.
export const cheDoToMode = (cheDo: TCheDoThuNo): number => (cheDo === 'custom' ? 2 : 1);
// Server trả số hoặc chuỗi đều nhận; không có / lạ -> null (đơn cũ chưa lưu chế độ)
export const modeToCheDo = (mode: any): TCheDoThuNo | null => {
  const m = String(mode ?? '').toLowerCase();
  if (m === '1' || m === 'auto') return 'auto';
  if (m === '2' || m === 'manual') return 'custom';
  return null;
};

// Tên 6 ô ở cột trái (đúng tên field trong formData thanh toán)
export const THU_NO_EDIT_KEYS = [
  'thuCongNoThietBi',
  'thuVat',
  'thuChenhLech',
  'thuGoiCuoc',
  'tienPhi',
  'tienCong'
] as const;

// TThuNoEditKey = tên 1 ô ('thuVat', 'tienPhi', ...)
export type TThuNoEditKey = typeof THU_NO_EDIT_KEYS[number];
// TThuNoThem = tiền của cả 6 ô
export type TThuNoThem = Record<TThuNoEditKey, number>;

export const EMPTY_THEM: TThuNoThem = {
  thuCongNoThietBi: 0,
  thuVat: 0,
  thuChenhLech: 0,
  thuGoiCuoc: 0,
  tienPhi: 0,
  tienCong: 0
};

// Tổng tiền nhập thêm của 6 ô
export const sumThem = (them: TThuNoThem): number => {
  return (
    Number(them?.thuCongNoThietBi || 0) +
    Number(them?.thuVat || 0) +
    Number(them?.thuChenhLech || 0) +
    Number(them?.thuGoiCuoc || 0) +
    Number(them?.tienPhi || 0) +
    Number(them?.tienCong || 0)
  );
};

// Kế toán gõ số vào 1 ô bên trái -> tính lại tiền nhập thêm của ô đó
// VD: đơn đã tick nợ công nợ TB 5tr, kế toán gõ 15tr -> nhập thêm = 10tr
//     kế toán gõ 3tr (thấp hơn 5tr) -> nhập thêm = 0, clamped = true để báo lỗi
export const applyThemEdit = (
  auto: any,
  them: TThuNoThem,
  key: TThuNoEditKey,
  value: number
): { them: TThuNoThem; clamped: boolean } => {
  const tienTuTinh = Number(auto?.[key] || 0); // tiền nợ của các đơn đã tick
  const tienGo = Number(value || 0); // số kế toán gõ

  let tienNhapThem = tienGo - tienTuTinh;
  if (tienNhapThem < 0) tienNhapThem = 0;

  return {
    them: { ...them, [key]: tienNhapThem },
    clamped: tienGo < tienTuTinh
  };
};

// Lấy tiền nợ từng ô của 1 đơn nợ (dòng trong ThuNoItems[].inDebtOrderCodes) — cộng giống reCalPaymentByThuNoOrder
// màn cập nhật không cộng công nợ TB cho dòng hoá đơn (type 2) còn màn tạo có cộng; ở đây theo màn tạo,
// số cuối cùng vẫn do reCalPaymentByThuNoOrder tính lại nên chỉ lệch thứ tự chọn đơn bỏ trong ca hiếm.
export const getTienNoTungO = (order: any): TThuNoThem => {
  const result = { ...EMPTY_THEM };

  // Nếu là đơn gộp (Hóa đơn), lấy mảng childItems. Nếu là đơn lẻ, bỏ vào mảng 1 phần tử.
  const itemsToProcess = order?.isGroup && order?.childItems?.length ? order.childItems : [order];

  itemsToProcess.forEach((item: any) => {
    result.thuVat += Number(item?.thuVat || 0);
    result.thuChenhLech += Number(item?.thuChenhLech || 0);
    result.thuGoiCuoc += Number(item?.goiCuoc || 0);
    result.tienPhi += Number(item?.tienPhi || 0);
    result.tienCong += Number(item?.tienCong || 0);

    // Tiền SP mọi loại đơn (kể cả Sửa chữa / Bảo hành / Tháo lắp) tính vào công nợ TB
    result.thuCongNoThietBi += Number(item?.tienSanPham || 0);
  });

  return result;
};

// Số tiền đang thu thiếu, tính trên đúng số gửi backend (> 0 = thiếu -> chặn tạo / cập nhật đơn).
// Tổng nợ các đơn đã tick so với tiền dùng để gạch nợ = tiền khoán + dư nợ dùng - phụ phí.
// VD: tick Hóa đơn 200k + Sửa chữa 2.5tr (tổng 2.7tr), tienKhoan 200k -> thiếu 2.5tr
//     tienKhoan 2.7tr + phụ phí 100k -> chỉ còn 2.6tr gạch nợ -> thiếu 100k
export const getSoTienThieu = (
  listOrderDebt: any[],
  tienKhoan: any,
  duNoKhachHang: any,
  tongTienOptions: any
): number => {
  let tongNo = 0;
  (listOrderDebt || []).forEach((item: any) => {
    tongNo += Number(item?.tienNo || 0);
  });

  const tienGachNo = Number(tienKhoan || 0) + Number(duNoKhachHang || 0) - Number(tongTienOptions || 0);
  return tongNo - tienGachNo;
};

// Tổng nợ của 1 đơn (cộng cả 6 ô)
const getTongNo = (order: any): number => {
  return sumThem(getTienNoTungO(order));
};

// Khoá 1 dòng đơn nợ: mã đơn và mã hoá đơn có thể trùng nên kèm loại (giống makeSelectKey ở XuLyDonNo / createOrders)
export const debtOrderKey = (o: any) => `${o?.type === 2 ? 'INV' : 'ORD'}_${o?.maDonHang}`;

export interface IRemovedOrder {
  maDonHang: string;
  amount: number;
  key: string;
}

// Thông báo ngắn khi bỏ nhiều đơn
// VD: "12 đơn (1,200,000 đ): DH1, DH2, DH3 và 9 đơn khác"
export const summarizeRemoved = (removed: IRemovedOrder[], max = 3): string => {
  let total = 0;
  removed.forEach(r => {
    total += Number(r.amount || 0);
  });

  const listMa = removed
    .slice(0, max)
    .map(r => r.maDonHang)
    .join(', ');

  let textConLai = '';
  if (removed.length > max) {
    textConLai = ` và ${removed.length - max} đơn khác`;
  }

  return `${removed.length} đơn (${total.toLocaleString('en-US')} đ): ${listMa}${textConLai}`;
};

// Kế toán sửa ô bên trái -> chọn lại đơn nợ từ đầu theo TỪNG Ô (bỏ qua số thực nhận).
// Đơn chỉ được tick khi MỌI ô đơn có nợ đều đã nhập và còn đủ tiền.
//
// VD: nhập Tiền SP = 3tr, VAT = 100k, Phí = 200k
//   DH001: TB 1tr + VAT 100k   -> đủ -> TICK (còn TB 2tr, VAT 0)
//   DH002: TB 1.5tr + phí 200k -> đủ -> TICK (còn TB 0.5tr, phí 0)
//   DH003: TB 2tr              -> TB chỉ còn 0.5tr -> KHÔNG tick
//   => nhập thêm = TB 0.5tr
//
// VD: chỉ nhập VAT = 400k
//   DH004: chỉ nợ VAT 300k     -> TICK (còn VAT 100k)
//   DH001: VAT 100k + TB 1tr   -> ô TB chưa nhập -> chỉ GỢI Ý, không tick
export const planCheckByColumns = (items: any[], target: any) => {
  // --- BƯỚC 1: COPY DANH SÁCH ĐƠN NỢ (không sửa thẳng dữ liệu form) ---
  const nextItems = (items || []).map((group: any) => ({
    ...group,
    inDebtOrderCodes: (group?.inDebtOrderCodes || []).map((order: any) => ({ ...order }))
  }));

  // Gom tất cả đơn của các nhóm vào 1 mảng cho dễ duyệt
  const allOrders: any[] = [];
  nextItems.forEach((group: any) => {
    group.inDebtOrderCodes.forEach((order: any) => allOrders.push(order));
  });

  // Nhớ các đơn đang tick -> cuối hàm so lại để biết đơn nào vừa bỏ / vừa thêm
  const keysCheckedBefore: string[] = [];
  allOrders.forEach(order => {
    if (order.checked) keysCheckedBefore.push(debtOrderKey(order));
  });

  // --- BƯỚC 2: TIỀN CÒN LẠI TỪNG Ô = SỐ KẾ TOÁN NHẬP ---
  const remaining: TThuNoThem = {
    thuCongNoThietBi: Math.max(0, Number(target?.thuCongNoThietBi || 0)),
    thuVat: Math.max(0, Number(target?.thuVat || 0)),
    thuChenhLech: Math.max(0, Number(target?.thuChenhLech || 0)),
    thuGoiCuoc: Math.max(0, Number(target?.thuGoiCuoc || 0)),
    tienPhi: Math.max(0, Number(target?.tienPhi || 0)),
    tienCong: Math.max(0, Number(target?.tienCong || 0))
  };

  // Các ô kế toán có nhập tiền (> 0)
  const enteredCols: string[] = [];
  THU_NO_EDIT_KEYS.forEach(key => {
    if (remaining[key] > 0) enteredCols.push(key);
  });

  // --- BƯỚC 3: BỎ TICK HẾT, ĐƠN TYPE 4/5 XÉT TRƯỚC (giống computeAutoCheck) ---
  allOrders.forEach(order => (order.checked = false));
  const priorityOrders = allOrders.filter(order => order.type === 4 || order.type === 5);
  const otherOrders = allOrders.filter(order => !(order.type === 4 || order.type === 5));
  const ordered = [...priorityOrders, ...otherOrders];

  // --- BƯỚC 4: DUYỆT TỪNG ĐƠN, ĐỦ TIỀN THÌ TICK ---
  ordered.forEach(order => {
    const tienNo = getTienNoTungO(order);

    // Các ô đơn này có nợ (VD DH001 -> thuCongNoThietBi, thuVat)
    const colsOfOrder = THU_NO_EDIT_KEYS.filter(key => tienNo[key] > 0);
    if (colsOfOrder.length === 0) return; // đơn không nợ ô nào

    let duTien = true;
    colsOfOrder.forEach(key => {
      if (!enteredCols.includes(key)) duTien = false; // ô này kế toán chưa nhập
      if (tienNo[key] > remaining[key]) duTien = false; // ô này không còn đủ tiền
    });
    if (!duTien) return;

    // Tick đơn + trừ tiền từng ô
    order.checked = true;
    colsOfOrder.forEach(key => {
      remaining[key] -= tienNo[key];
    });
  });

  // --- BƯỚC 5: GỢI Ý ---
  // Đơn chưa tick, nợ ở ô đã nhập vẫn đủ tiền, nhưng còn nợ ở ô kế toán chưa nhập
  const suggested: string[] = [];
  allOrders.forEach(order => {
    if (order.checked) return;

    const tienNo = getTienNoTungO(order);
    const colsOfOrder = THU_NO_EDIT_KEYS.filter(key => tienNo[key] > 0);

    let coODaNhap = false;
    let coOChuaNhap = false;
    let duTien = true;
    colsOfOrder.forEach(key => {
      if (enteredCols.includes(key)) {
        coODaNhap = true;
        if (tienNo[key] > remaining[key]) duTien = false;
      } else {
        coOChuaNhap = true;
      }
    });

    if (coODaNhap && coOChuaNhap && duTien) suggested.push(debtOrderKey(order));
  });

  // --- BƯỚC 6: KẾT QUẢ ---
  // removed / added: đơn vừa bỏ / vừa thêm, để hiện thông báo + nút Hoàn tác
  const removed: IRemovedOrder[] = [];
  const added: IRemovedOrder[] = [];
  allOrders.forEach(order => {
    const key = debtOrderKey(order);
    const wasChecked = keysCheckedBefore.includes(key);
    const info = { maDonHang: order.maDonHang, amount: getTongNo(order), key };

    if (!order.checked && wasChecked) removed.push(info);
    if (order.checked && !wasChecked) added.push(info);
  });

  // them = tiền còn lại từng ô (chính là phần nhập thêm)
  return { items: nextItems, them: remaining, removed, added, suggested };
};

// Gom mọi đơn của các nhóm, đơn type 4/5 xếp trước (cùng thứ tự với lúc tick đơn)
const getDonTheoThuTu = (items: any[]): any[] => {
  const uuTien: any[] = [];
  const conLai: any[] = [];
  (items || []).forEach((group: any) => {
    (group?.inDebtOrderCodes || []).forEach((order: any) => {
      if (order?.type === 4 || order?.type === 5) uuTien.push(order);
      else conLai.push(order);
    });
  });
  return [...uuTien, ...conLai];
};

// Tìm 1 dòng đơn nợ theo khoá
export const findDebtOrder = (items: any[], key: string | null | undefined): any => {
  let found: any = null;
  if (!key) return found;
  (items || []).forEach((group: any) => {
    (group?.inDebtOrderCodes || []).forEach((order: any) => {
      if (debtOrderKey(order) === key) found = order;
    });
  });
  return found;
};

// Đơn mặc định nhận tiền dư: đơn CHƯA tick đầu tiên (bỏ qua đơn đã thu đủ TM + CK, không chọn được)
//   - Tự động: đơn đầu tiên còn nợ
//   - Tùy chỉnh: đơn đầu tiên có nợ ở ít nhất 1 cột còn dư trong 6 ô (tiền ngoài ô không dùng trả một phần)
// VD Tùy chỉnh: dư Chênh lệch 300k; DH01 chỉ nợ VAT, DH02 nợ Chênh lệch 500k -> chọn DH02
// Không có tiền dư / không có đơn hợp lệ -> null (toàn bộ tiền dư vào dư nợ)
export const pickAllocKey = (items: any[], tienDu: number, them: TThuNoThem, cheDo: TCheDoThuNo): string | null => {
  if (cheDo === 'auto' && Number(tienDu || 0) <= 0) return null;
  if (cheDo === 'custom' && sumThem(them) <= 0) return null;

  let key: string | null = null;
  getDonTheoThuTu(items).forEach(order => {
    if (key) return; // đã chọn được
    if (order?.checked) return;
    if (getPaymentStatus(order) === 'both-done') return;

    const tienNo = getTienNoTungO(order);
    let hopLe = false;
    THU_NO_EDIT_KEYS.forEach(col => {
      if (tienNo[col] <= 0) return;
      if (cheDo === 'auto') hopLe = true;
      else if (Number(them?.[col] || 0) > 0) hopLe = true;
    });
    if (hopLe) key = debtOrderKey(order);
  });
  return key;
};

// Mức phân bổ tối đa từng cột (xét riêng từng cột) = nợ cột đó của đơn, và không vượt:
//   - Tự động: tiền dư
//   - Tùy chỉnh: dư của cột đó trong 6 ô (them). Tiền thực nhận ngoài 6 ô không trả một phần, vào thẳng dư nợ
// Dùng cho ô sửa tay ở bảng đơn nợ. VD Tự động: dư 200k, đơn nợ TB 50k + VAT 300k -> TB 50k, VAT 200k
// VD Tùy chỉnh: gõ Thu SP 4.1tr, đơn tick 3.6tr (dư SP 500k), đơn nợ SP 1.2tr -> tối đa 500k (còn nợ 700k)
export const mucToiDaTungCot = (order: any, tienDu: number, them: TThuNoThem, cheDo: TCheDoThuNo): TThuNoThem => {
  const result: TThuNoThem = { ...EMPTY_THEM };
  if (!order) return result;

  const tienNo = getTienNoTungO(order);
  const tienDuTong = Math.max(0, Math.round(Number(tienDu || 0)));
  THU_NO_EDIT_KEYS.forEach(key => {
    let toiDa = Math.max(0, Math.round(tienNo[key]));
    if (cheDo === 'custom') toiDa = Math.min(toiDa, Math.max(0, Math.round(Number(them?.[key] || 0))));
    else toiDa = Math.min(toiDa, tienDuTong);
    result[key] = toiDa;
  });

  return result;
};

// Tiền phân bổ từng cột vào 1 đơn chưa tick.
// Mỗi cột tối đa theo mucToiDaTungCot; Tự động thì tổng các cột còn không vượt tiền dư,
// rải theo thứ tự cột TB -> VAT -> Chênh lệch -> Gói cước -> Phí -> Công.
// suaTay: số kế toán tự sửa từng cột, vẫn bị chặn trong các mức trên.
// VD Tự động: dư 200k, đơn nợ TB 50k + VAT 300k -> TB 50k, VAT 150k
// VD Tùy chỉnh: dư VAT 500k + Phí 100k, đơn nợ VAT 300k (không nợ phí) -> VAT 300k (Phí 100k vào dư nợ)
export const tinhPhanBoTungCot = (
  order: any,
  tienDu: number,
  them: TThuNoThem,
  cheDo: TCheDoThuNo,
  suaTay?: TThuNoThem | null
): TThuNoThem => {
  const result: TThuNoThem = { ...EMPTY_THEM };
  if (!order) return result;

  const muc = mucToiDaTungCot(order, tienDu, them, cheDo);
  let tienDuConLai = Math.max(0, Math.round(Number(tienDu || 0)));

  THU_NO_EDIT_KEYS.forEach(key => {
    // --- BƯỚC 1: MỨC TỐI ĐA CỦA CỘT (Tùy chỉnh: mucToiDaTungCot đã chặn theo dư của cột) ---
    let toiDa = muc[key];
    if (cheDo === 'auto') toiDa = Math.min(toiDa, tienDuConLai);

    // --- BƯỚC 2: SỐ CỦA CỘT = MỨC TỐI ĐA, HOẶC SỐ SỬA TAY (KHÔNG VƯỢT MỨC TỐI ĐA) ---
    let tien = toiDa;
    if (suaTay) tien = Math.min(Math.max(0, Math.round(Number(suaTay[key] || 0))), toiDa);

    result[key] = tien;
    tienDuConLai -= tien;
  });

  return result;
};

// Body `listPaymentAllocations` (gửi backend): tiền dư trừ vào 1 đơn CHƯA tick (allocKey).
// Không chọn đơn / đơn đã tick / tiền phân bổ = 0 -> [] (toàn bộ tiền dư vào dư nợ).
// shortcut: đơn nợ không có field saleAccount -> lấy nguoiTao; nhóm hoá đơn lấy id nhóm hoặc id hoá đơn con đầu. Chờ server xác nhận.
export const buildPaymentAllocations = (
  items: any[],
  allocKey: string | null | undefined,
  tienDu: number,
  them: TThuNoThem,
  cheDo: TCheDoThuNo = 'auto',
  suaTay?: TThuNoThem | null
) => {
  // --- BƯỚC 1: TÌM ĐƠN ĐƯỢC CHỌN ---
  const order = findDebtOrder(items, allocKey);
  if (!order || order.checked) return [];

  // --- BƯỚC 2: TIỀN TỪNG CỘT ---
  const alloc = tinhPhanBoTungCot(order, tienDu, them, cheDo, suaTay);

  // ===== BODY GỬI BACKEND (tên field backend khác tên ô) =====
  const tienSanPham = alloc.thuCongNoThietBi;
  const tienPhi = alloc.tienPhi;
  const tienVat = alloc.thuVat;
  const tienChenhLech = alloc.thuChenhLech;
  const tienGoiCuoc = alloc.thuGoiCuoc;
  const tienCong = alloc.tienCong;
  const tongTien = tienSanPham + tienPhi + tienVat + tienChenhLech + tienGoiCuoc + tienCong;
  if (tongTien <= 0) return [];

  return [
    {
      type: Number(order.type || 0),
      maDonHang: order.maDonHang || '',
      invoiceId: Number(order.id || order.childItems?.[0]?.id || 0),
      saleAccount: order.saleAccount || order.nguoiTao || '',
      tienSanPham,
      tienPhi,
      tienVat,
      tienChenhLech,
      tienGoiCuoc,
      tienCong,
      tienPhanMem: 0,
      tienChietKhau: 0,
      tienKhac: 0,
      tongTien
    }
  ];
};

// Đổi body phân bổ (tên field backend) về tiền theo 6 ô
export const allocToCols = (alloc: any): TThuNoThem => ({
  thuCongNoThietBi: Number(alloc?.tienSanPham || 0),
  thuVat: Number(alloc?.tienVat || 0),
  thuChenhLech: Number(alloc?.tienChenhLech || 0),
  thuGoiCuoc: Number(alloc?.tienGoiCuoc || 0),
  tienPhi: Number(alloc?.tienPhi || 0),
  tienCong: Number(alloc?.tienCong || 0)
});

// Phần cộng thêm vào 6 ô khi gửi đi (thuVat, ...) = max(phần nhập thêm, tiền phân bổ) từng cột.
// Ô gửi đi = nợ đơn tick + phần này -> tiền trả một phần vẫn nằm trong ô, allocation chỉ ghi phần trả một phần.
// VD Tự động: đơn tick VAT 200k, phân bổ VAT 100k (them 0) -> thuVat gửi 300k, allocation tienVat 100k
// VD Tùy chỉnh: nhập VAT 300k (them 100k) + phân bổ VAT 100k -> vẫn 300k (phân bổ đã nằm trong phần nhập, không cộng 2 lần)
// Đơn đã lưu (6 ô server đã gồm phân bổ) thì không gọi hàm này, dùng them như cũ.
// Tùy chỉnh: ô giữ đúng số kế toán gõ, tiền trả một phần chỉ ghi ở allocation (phần dư còn lại vào dư nợ)
// VD: gõ Thu SP 4.1tr (đơn tick 3.6tr -> them 500k), trả một phần SP 1.2tr -> ô vẫn 4.1tr, không nhảy 4.8tr
export const getThemGui = (them: TThuNoThem, alloc: any, cheDo: TCheDoThuNo = 'auto'): TThuNoThem => {
  if (cheDo === 'custom') return { ...EMPTY_THEM, ...them };
  const cot = allocToCols(alloc);
  return {
    thuCongNoThietBi: Math.max(Number(them?.thuCongNoThietBi || 0), cot.thuCongNoThietBi),
    thuVat: Math.max(Number(them?.thuVat || 0), cot.thuVat),
    thuChenhLech: Math.max(Number(them?.thuChenhLech || 0), cot.thuChenhLech),
    thuGoiCuoc: Math.max(Number(them?.thuGoiCuoc || 0), cot.thuGoiCuoc),
    tienPhi: Math.max(Number(them?.tienPhi || 0), cot.tienPhi),
    tienCong: Math.max(Number(them?.tienCong || 0), cot.tienCong)
  };
};

// Phân bổ đã lưu của đơn: server trả field `allocations` (loại đơn ở `targetType`),
// còn lúc gửi đi là `listPaymentAllocations` (loại ở `type`). Không có -> null
export const getPhanBoDaLuu = (row: any): any => {
  const alloc = row?.listPaymentAllocations?.[0] || row?.allocations?.[0];
  if (!alloc) return null;
  return { ...alloc, type: alloc.type ?? alloc.targetType };
};

// Số liệu cho form xác nhận đơn thu nợ (dùng chung màn tạo + cập nhật)
// VD: thực nhận 2.5tr + dư nợ dùng 500k = 3tr; tick 2 đơn 2tr; phân bổ 700k vào DH09 (nợ 1tr)
//     -> DH09 còn nợ 300k, dư nợ còn lại = 3tr - 2tr - 700k = 300k
export const buildThuNoXacNhan = ({
  items,
  tienThucNhan,
  duNoDung,
  phuPhi,
  chiTiet,
  allocation
}: {
  items: any[];
  tienThucNhan: number;
  duNoDung: number;
  phuPhi: number;
  chiTiet: TThuNoThem;
  allocation: any;
}) => {
  // --- BƯỚC 1: TIỀN DÙNG ĐỂ THANH TOÁN ---
  const thucNhan = Math.max(0, Number(tienThucNhan || 0));
  const duNo = Math.max(0, Number(duNoDung || 0));
  const phi = Math.max(0, Number(phuPhi || 0));
  const tongTien = thucNhan + duNo;
  const tienGachNo = Math.max(0, tongTien - phi);

  // --- BƯỚC 2: CÁC ĐƠN ĐƯỢC THANH TOÁN ĐỦ (ĐÃ TICK) ---
  const donThanhToan: Array<{ key: string; maDonHang: string; loai: string; tienNo: number }> = [];
  let tongDonThanhToan = 0;
  getDonTheoThuTu(items).forEach(order => {
    if (!order?.checked) return;
    const tienNo = Number(order.tienNo || 0);
    donThanhToan.push({
      key: debtOrderKey(order),
      maDonHang: order.maDonHang || '',
      loai: order.orderType || order.loai || '',
      tienNo
    });
    tongDonThanhToan += tienNo;
  });

  // --- BƯỚC 3: ĐƠN ĐƯỢC THANH TOÁN MỘT PHẦN (PHÂN BỔ) ---
  let phanBo: any = null;
  let tienPhanBo = 0;
  if (allocation) {
    const order = findDebtOrder(items, debtOrderKey(allocation));
    tienPhanBo = Number(allocation.tongTien || 0);
    const tongNo = Number(order?.tienNo || 0);
    phanBo = {
      maDonHang: allocation.maDonHang || '',
      loai: order?.orderType || order?.loai || '',
      tien: tienPhanBo,
      tongNo,
      noConLai: Math.max(0, tongNo - tienPhanBo),
      cot: allocToCols(allocation)
    };
  }

  // --- BƯỚC 4: DƯ NỢ CÒN LẠI / THU THIẾU ---
  // Tiền đã dùng = đơn tick + trả một phần. Ô nhập dư hơn đơn (Tùy chỉnh) không gạch đơn nào -> vào dư nợ
  // VD: VAT nhập 972,002 (đơn nợ 972,000) -> 2đ vào dư nợ cùng phần tiền chưa nhập ô
  const daDung = tongDonThanhToan + tienPhanBo;
  const duNoConLai = Math.max(0, tienGachNo - daDung);
  const thieu = Math.max(0, Math.max(sumThem(chiTiet), daDung) - tienGachNo);

  return {
    tienThucNhan: thucNhan,
    duNoDung: duNo,
    phuPhi: phi,
    tongTien,
    donThanhToan,
    tongDonThanhToan,
    phanBo,
    duNoConLai,
    thieu,
    chiTiet
  };
};

// Mở lại đơn đã lưu: phần dư từng cột = số đã lưu - tiền nợ của các đơn đã lưu (không âm)
export const restoreThem = (saved: any, auto: any): TThuNoThem => {
  return {
    thuCongNoThietBi: Math.max(0, Number(saved?.thuCongNoThietBi || 0) - Number(auto?.thuCongNoThietBi || 0)),
    thuVat: Math.max(0, Number(saved?.thuVat || 0) - Number(auto?.thuVat || 0)),
    thuChenhLech: Math.max(0, Number(saved?.thuChenhLech || 0) - Number(auto?.thuChenhLech || 0)),
    thuGoiCuoc: Math.max(0, Number(saved?.thuGoiCuoc || 0) - Number(auto?.thuGoiCuoc || 0)),
    tienPhi: Math.max(0, Number(saved?.tienPhi || 0) - Number(auto?.tienPhi || 0)),
    tienCong: Math.max(0, Number(saved?.tienCong || 0) - Number(auto?.tienCong || 0))
  };
};
